import { useState } from "react";
import { X } from "lucide-react";
import { useNeumorphicTheme } from "@/contexts/NeumorphicThemeContext";
import { cn } from "@/lib/utils";

const UDAFeatureAccordion = () => {
  const [activeCard, setActiveCard] = useState<number | null>(null);
  const [showCalendly, setShowCalendly] = useState(false);
  
  // Try to get theme, provide fallback for pages without provider
  let theme = "dark";
  try {
    const themeContext = useNeumorphicTheme();
    theme = themeContext.theme;
  } catch {
    // Not wrapped in provider, use default
  }

  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const features = [{
    title: "Custom ADU Builder in California",
    description: "Every property is unique — and so is every ADU we build. Our in-house architects create custom Accessory Dwelling Units that match your lot, lifestyle, and goals, with local code compliance and tailored design in every square foot."
  }, {
    title: "Fully Permitted ADU Contractor (Bay Area)",
    description: "We handle the entire permitting process for you — city research, planning review, submittals, and approvals — for cities across the Bay Area and California. No red tape, no guesswork, just permit-ready results."
  }, {
    title: "Garage Conversion Experts with Local Zoning Knowledge",
    description: "Thinking of converting your garage? We specialize in legal garage-to-ADU conversions that meet city code and maximize your livable space — with full zoning checks, setbacks, and parking compliance built in."
  }, {
    title: "Pre-Approved, Investment-Optimized ADU Plans",
    description: "Need to build fast? Choose from our collection of pre-approved ADU plans — designed for high ROI, local approval speed, and minimal change orders. Start building months ahead of schedule."
  }, {
    title: "ROI-Backed ADU Design with Transparent Pricing",
    description: "Every design we offer includes projected rental income, build cost, and breakeven time. Our clients know their return before signing a contract — and can choose finishes to match budget and strategy."
  }, {
    title: "One-Stop ADU Design-Build Firm",
    description: "Skip the handoffs. Our full-service team handles design, engineering, permitting, and construction under one roof. No subcontractor chaos. Just seamless, on-time, on-budget delivery."
  }, {
    title: "Build a Rental Unit in Your Backyard — Start to Finish",
    description: "Whether you're new to rentals or building your second unit, we guide you through the entire process — from site layout to final inspection — to create a profitable rental space in your own backyard."
  }, {
    title: "ADUs for Multigenerational Living or Passive Income",
    description: "Need space for family — or future cash flow? We design ADUs that flex with your life. Private entries, flexible layouts, and income-generating upgrades are all part of your plan."
  }, {
    title: "ADU Calculator & ROI Wizard — Free & Instant",
    description: "Know your numbers before you build. Use our ADU Cost Calculator or ROI Wizard to estimate budget, income, and value-add — specific to your address, zoning, and goals."
  }, {
    title: "Accessory Dwelling Units Tailored to Your Lot + Goals",
    description: "No two lots are alike. That's why every ADU we build is planned for your setbacks, slope, access, and lifestyle needs — not just slapped on a prefab template."
  }, {
    title: "Maximize Your Property's Value and Monthly Income",
    description: "Our ADUs don't just add space — they add equity and cash flow. On average, our clients increase property value by $150–400K and generate $2,500–3,800 in monthly rental income."
  }, {
    title: "ADU Construction Services With Guaranteed ROI Insights",
    description: "We're the only ADU builder that runs ROI simulations before we break ground. Our team blends finance and design to ensure your unit isn't just beautiful — it's profitable."
  }];

  const handleMobileCardClick = (index: number) => {
    setActiveCard(activeCard === index ? null : index);
  };

  // Neumorphic card styles - theme aware with smooth transitions
  const cardStyles = cn(
    "rounded-2xl p-6 transition-all duration-500 hover:translate-y-[-2px]",
    theme === "dark"
      ? "bg-[#0a0a0a] shadow-[12px_12px_24px_#000000,-12px_-12px_24px_#141414] hover:shadow-[16px_16px_32px_#000000,-16px_-16px_32px_#141414]"
      : "bg-[#f5f5f5] shadow-[8px_8px_16px_#d8d8d8,-8px_-8px_16px_#fefefe] hover:shadow-[10px_10px_20px_#d0d0d0,-10px_-10px_20px_#fefefe]"
  );

  // Neumorphic button styles - theme aware with smooth transitions
  const primaryButtonStyles = cn(
    "rounded-xl px-4 py-2 text-sm font-medium cursor-pointer transition-all duration-500",
    theme === "dark"
      ? "bg-[#0a0a0a] shadow-[6px_6px_12px_#000000,-6px_-6px_12px_#141414] hover:shadow-[8px_8px_16px_#000000,-8px_-8px_16px_#141414] text-white"
      : "bg-[#f5f5f5] shadow-[4px_4px_8px_#d8d8d8,-4px_-4px_8px_#fefefe] hover:shadow-[6px_6px_10px_#d0d0d0,-6px_-6px_10px_#fefefe] text-[#1a1a1a]"
  );

  const secondaryButtonStyles = cn(
    "rounded-xl px-4 py-2 text-sm font-medium cursor-pointer transition-all duration-500",
    theme === "dark"
      ? "bg-[#0a0a0a] shadow-[6px_6px_12px_#000000,-6px_-6px_12px_#141414] hover:shadow-[8px_8px_16px_#000000,-8px_-8px_16px_#141414] text-gray-300"
      : "bg-[#f5f5f5] shadow-[4px_4px_8px_#d8d8d8,-4px_-4px_8px_#fefefe] hover:shadow-[6px_6px_10px_#d0d0d0,-6px_-6px_10px_#fefefe] text-[#4a4a4a]"
  );

  // Mobile card styles - theme aware with smooth transitions
  const mobileCardStyles = cn(
    "rounded-xl p-4 cursor-pointer transition-all duration-500",
    theme === "dark"
      ? "bg-[#0a0a0a] shadow-[8px_8px_16px_#000000,-8px_-8px_16px_#141414] active:shadow-[inset_4px_4px_8px_#000000,inset_-4px_-4px_8px_#141414]"
      : "bg-[#f5f5f5] shadow-[6px_6px_12px_#d8d8d8,-6px_-6px_12px_#fefefe] active:shadow-[inset_3px_3px_6px_#e0e0e0,inset_-3px_-3px_6px_#fefefe]"
  );

  // Text colors - theme aware with smooth transitions
  const titleTextColor = cn("transition-colors duration-500", theme === "dark" ? "text-white" : "text-[#1a1a1a]");
  const descriptionTextColor = cn("transition-colors duration-500", theme === "dark" ? "text-gray-300" : "text-[#3a3a3a]");

  // Mobile content overlay styles with smooth transitions
  const mobileOverlayStyles = cn(
    "transition-all duration-500",
    theme === "dark"
      ? "bg-[#0a0a0a] shadow-[8px_8px_16px_#000000,-8px_-8px_16px_#141414]"
      : "bg-[#f5f5f5] shadow-[6px_6px_12px_#d8d8d8,-6px_-6px_12px_#fefefe]"
  );

  return (
    <>
      <div className="w-full max-w-7xl mx-auto px-4 py-[24px] my-[29px]">
        <h3 className="mb-8 text-center text-[#e4683b] py-[8px] my-[17px] text-3xl md:text-4xl font-normal">
          What Makes UDA ADU the Most Strategic ADU Builder in California?
        </h3>
        
        {/* Desktop Version - Neumorphic raised cards with raised buttons */}
        <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div key={index} className={cardStyles}>
              <h4 className="text-base font-medium leading-tight text-left text-[#e4683b] mb-3">
                {feature.title}
              </h4>
              <p className={cn("text-sm leading-relaxed mb-4", descriptionTextColor)}>
                {feature.description}
              </p>
              <div className="flex gap-3 pt-2">
                <button 
                  onClick={() => window.open('https://www.udaadu.com/landing', '_blank')}
                  className={primaryButtonStyles}
                >
                  What fits my lot?
                </button>
                <button 
                  onClick={openCalendlyModal}
                  className={secondaryButtonStyles}
                >
                  Schedule
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile Version - Neumorphic raised cards */}
        <div className="md:hidden space-y-4">
          {features.map((feature, index) => (
            <div key={index} className="relative">
              <div 
                onClick={() => handleMobileCardClick(index)} 
                className={mobileCardStyles}
              >
                <h4 className={cn("font-medium leading-tight flex items-center justify-between text-left text-base", titleTextColor)}>
                  {feature.title}
                  <span className="text-[#E4683B] text-xs ml-2">
                    {activeCard === index ? '−' : '+'}
                  </span>
                </h4>
                
                {/* Show buttons when card is active */}
                {activeCard === index && (
                  <div className="flex gap-3 mt-4">
                    <button 
                      className={primaryButtonStyles}
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open('https://www.udaadu.com/landing', '_blank');
                      }}
                    >
                      What fits my lot?
                    </button>
                    <button 
                      className={secondaryButtonStyles}
                      onClick={(e) => {
                        e.stopPropagation();
                        openCalendlyModal();
                      }}
                    >
                      Schedule
                    </button>
                  </div>
                )}
              </div>
              
              {/* Content overlay that appears when card is active */}
              {activeCard === index && (
                <div className={cn("absolute top-full left-0 right-0 z-50 mt-2 rounded-xl p-4 animate-in slide-in-from-top-2 duration-200", mobileOverlayStyles)}>
                  <p className={cn("text-sm leading-relaxed", descriptionTextColor)}>
                    {feature.description}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-[#f5f5f5] rounded-2xl overflow-hidden shadow-[16px_16px_32px_#000000,-16px_-16px_32px_#1a1a1a]">
            <button 
              onClick={closeCalendlyModal} 
              className="absolute top-4 right-4 z-10 p-2 bg-[#f5f5f5] rounded-full shadow-[4px_4px_8px_#c0c0c0,-4px_-4px_8px_#ffffff] hover:shadow-[6px_6px_12px_#b0b0b0,-6px_-6px_12px_#ffffff] transition-all"
            >
              <X className="h-6 w-6 text-[#3a3a3a]" />
            </button>
            <iframe 
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" 
              width="100%" 
              height="100%" 
              frameBorder="0" 
              title="Schedule a meeting" 
            />
          </div>
        </div>
      )}
    </>
  );
};

export default UDAFeatureAccordion;
